/**
 * Hosted Agent Hooks
 *
 * React Query hooks for hosted agent CRUD, health checks, and diagnostics.
 * Uses connections.json hostedAgentConnections for persistence.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useConnectionsJson, useSaveConnectionsJson, useSaveDraftConnectionsJson } from '@/hooks/useConnections';
import { useEnvironmentSettings } from '@/hooks/useEnvironmentSettings';
import { useAppStore } from '@/stores/appStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useHostedAgentDeletionStore, hostedAgentDeletionKey } from '@/stores/hostedAgentDeletionStore';
import type {
  HealthCheckResponse,
  HostedAgentConnection,
  HostedAgentStatus,
  SetupProgress,
} from '@/types/hostedAgent';
import {
  DEFAULT_HOSTED_AGENT_IMAGE,
  HOSTED_AGENT_IMAGE_PREFIX,
  HOSTED_AGENT_IMAGE_SETTING,
  ROLE_COGNITIVE_SERVICES_OPENAI_USER,
} from '@/types/hostedAgent';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import {
  fetchConnectionsDraftJson,
  fetchConnectionsDraftJsonStrictWithETag,
  fetchConnectionsJsonStrictWithETag,
  saveConnectionsJson,
  saveDraftConnectionsJson,
} from '@/services/logicAppsApi';
import {
  buildAccountScope,
  deleteHostedAgent,
  removeRbacAssignments,
  runDiagnostics,
  runHealthCheck,
  type DiagnosticResults,
} from '@/services/hostedAgentService';
import { isLocalMode } from '@/services/localRuntimeClient';
import { GUID_PATTERN } from '@/utils/primitives';
import { sanitizeErrorMessage } from '@/utils/sanitization/errorMessage';

import { useEffect, useMemo } from 'react';
import { telemetryService } from '@/services/telemetryService';

// =============================================================================
// SHARED HELPERS
// =============================================================================

/**
 * Builds the appInfo needed by useConnectionsJson/useSaveConnectionsJson.
 * Uses natural prerequisites: if a project/app is selected (cloud mode), use it.
 *
 * When nothing is selected yet:
 *  - Local mode: fall back to synthetic 'local' info so the query fires — the
 *    service layer routes to /local/connections when isLocalMode() is true.
 *  - Cloud mode: return null so useConnectionsJson stays disabled (enabled gate
 *    requires projectName/appName) instead of firing a bogus request against
 *    /automationProjects/local/applications/local/... during initial load.
 */
export function useAppInfoForConnections() {
  const { selectedProject, selectedApp } = useAppStore();

  if (selectedProject && selectedApp) {
    return { projectName: selectedProject, appName: selectedApp.name, appId: selectedApp.properties.appResourceId };
  }

  // Only synthesize 'local' info in local mode; in cloud mode wait for real app context.
  if (isLocalMode()) {
    return { projectName: 'local', appName: 'local', appId: 'local' };
  }

  return null;
}

/**
 * Resolve the current connections config for a mutation, refetching if the
 * React Query cache hasn't populated yet.
 *
 * The hosted-agent mutations persist the *whole* connections.json (spreading
 * the current config + their hostedAgentConnections change). If we seed that
 * from an empty baseline while the cache is still loading (`data === undefined`),
 * we'd overwrite connections.json with a file that drops every other connection
 * type (managed APIs, service providers, MCP, etc.) — silent data loss.
 *
 * A loaded-but-empty config (`{}`) is a valid baseline and returned as-is; only
 * `undefined` (not yet loaded) triggers a refetch. If it still can't load, throw
 * so the caller aborts rather than persisting an incomplete file.
 *
 * Exported for unit testing.
 */
export async function resolveConnectionsConfig<T>(
  query: { data: T | undefined; refetch: () => Promise<{ data: T | undefined }> },
): Promise<T> {
  if (query.data !== undefined) return query.data;
  const refetched = await query.refetch();
  if (refetched.data !== undefined) return refetched.data;
  throw new Error(
    'Unable to load the current connections configuration; aborting to avoid overwriting existing connections.',
  );
}

/**
 * Read the CURRENT connections-draft.json straight from the service, bypassing the
 * React Query cache (which has a 5-minute staleTime and is not invalidated by the
 * designer's autosave). A fresh read is required so a hosted-agent write seeds its
 * draft payload from the real draft file — otherwise draft-only connections (e.g. an
 * OpenAI agentConnection created while the workflow is in draft mode) would be missing
 * from the baseline and dropped when the draft file is rewritten.
 *
 * `fetchConnectionsDraftJson` already falls back to connections.json on 404/403, so a
 * workflow that has no draft file yet still yields a safe, non-empty baseline.
 *
 * Exported for unit testing.
 */
export async function readDraftConnectionsBaseline(
  appInfo: { projectName: string; appName: string } | null,
  getToken: () => Promise<string | null>,
): Promise<ConnectionsConfiguration> {
  if (!appInfo?.projectName || !appInfo?.appName) return {};
  return fetchConnectionsDraftJson({
    projectName: appInfo.projectName,
    appName: appInfo.appName,
    getToken,
  });
}

/**
 * Build the production + draft connections payloads for a hosted-agent write.
 *
 * Each file is seeded from its OWN baseline (production from connections.json, draft
 * from connections-draft.json) so rewriting the draft file never clobbers draft-only
 * connections. `applyHosted` receives a shallow copy of each file's
 * `hostedAgentConnections` map and returns the updated map (add/update/delete).
 *
 * Exported for unit testing.
 */
export function buildHostedAgentPayloads(
  prodConfig: ConnectionsConfiguration,
  draftConfig: ConnectionsConfiguration,
  applyHosted: (
    hosted: NonNullable<ConnectionsConfiguration['hostedAgentConnections']>,
  ) => NonNullable<ConnectionsConfiguration['hostedAgentConnections']>,
): { prod: ConnectionsConfiguration; draft: ConnectionsConfiguration } {
  return {
    prod: { ...prodConfig, hostedAgentConnections: applyHosted({ ...(prodConfig.hostedAgentConnections ?? {}) }) },
    draft: { ...draftConfig, hostedAgentConnections: applyHosted({ ...(draftConfig.hostedAgentConnections ?? {}) }) },
  };
}

/**
 * Transient server statuses worth retrying on the post-Foundry local cleanup.
 * The canary VFS endpoint intermittently returns 502/503/504; because the agent
 * is already gone from Foundry by this point, a whole-file connections rewrite is
 * idempotent and safe to re-attempt.
 */
const TRANSIENT_STATUSES = new Set([502, 503, 504]);

function isTransientServerError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' && TRANSIENT_STATUSES.has(status);
}

/**
 * An optimistic-concurrency conflict: the file changed between our baseline read
 * and our conditional (If-Match) write. Resolve by re-reading and re-applying.
 */
function isConcurrencyConflict(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 412;
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * The deployWorkflowArtifacts endpoint is serialized, so rapid consecutive
 * hosted-agent status writes can return a transient 5xx (502/503/504). Writes
 * retry with an increasing backoff (10s, 15s, 20s, 25s) before surfacing the
 * failure — but each retry RE-READS the file and re-applies the update (see
 * `useUpdateHostedAgent`) so a retry never replays a stale whole-file snapshot
 * over a concurrent change made during the backoff.
 */
const UPDATE_RETRY_DELAYS_MS = [10000, 15000, 20000, 25000];

/**
 * Whether a hosted-agent entry already reflects every field in `updates`. Lets a
 * retry skip the write when a false-negative transient 5xx actually landed, and
 * avoids clobbering a concurrent change that already satisfies the update.
 */
function hostedAgentUpdatesApplied(
  entry: HostedAgentConnection,
  updates: Partial<HostedAgentConnection>,
): boolean {
  return (Object.keys(updates) as (keyof HostedAgentConnection)[]).every(
    (key) => JSON.stringify(entry[key]) === JSON.stringify(updates[key]),
  );
}

// =============================================================================
// QUERY KEYS
// =============================================================================

export const hostedAgentKeys = {
  all: ['hostedAgents'] as const,
  list: () => [...hostedAgentKeys.all, 'list'] as const,
  detail: (name: string) => [...hostedAgentKeys.all, 'detail', name] as const,
  health: (name: string) => [...hostedAgentKeys.all, 'health', name] as const,
  diagnostics: (name: string) => [...hostedAgentKeys.all, 'diagnostics', name] as const,
};

export type HostedAgentDeletePhase = 'rbac' | 'foundry' | 'local';
export type HostedAgentDeleteProgressStatus = 'started' | 'completed' | 'warning' | 'failed';

export interface HostedAgentDeleteProgress {
  phase: HostedAgentDeletePhase;
  status: HostedAgentDeleteProgressStatus;
  error?: string;
}

export interface HostedAgentDeleteWarning {
  phase: 'rbac';
  identity: 'blueprint' | 'instance' | 'all';
  message: string;
}

export interface HostedAgentDeleteResult {
  warnings: HostedAgentDeleteWarning[];
}

export interface DeleteHostedAgentParams {
  key: string;
  connection: HostedAgentConnection;
  onProgress?: (progress: HostedAgentDeleteProgress) => void;
}

// =============================================================================
// NORMALIZATION HELPERS
// =============================================================================

const KNOWN_STATUSES = [
  'connected', 'provisioning', 'auth_failed', 'agent_error',
  'version_mismatch', 'unreachable', 'unknown',
] as const satisfies readonly HostedAgentStatus[];

const KNOWN_SETUP_PROGRESS = [
  'agent_created', 'identities_discovered', 'rbac_assigned', 'complete',
] as const satisfies readonly SetupProgress[];

/**
 * connections.json is user-/backend-authored, so status/setupProgress casing and
 * surrounding whitespace may not match the exact lowercase unions (e.g. "Connected",
 * "Complete", "RBAC_Assigned", "connected\n"). Trim + lowercase on load so the
 * badge/label mappings, status switches, and health-poll gating stay resilient to
 * casing/whitespace differences and older data. Unrecognized values fall back to a
 * safe default rather than an unmapped value that renders blank.
 */
function normalizeStatus(raw: unknown): HostedAgentStatus {
  const value = String(raw ?? '').trim().toLowerCase();
  return (KNOWN_STATUSES as readonly string[]).includes(value)
    ? (value as HostedAgentStatus)
    : 'unknown';
}

function normalizeSetupProgress(raw: unknown): SetupProgress {
  const value = String(raw ?? '').trim().toLowerCase();
  // Unknown values fall back to the earliest state (SetupProgress has no 'unknown'
  // sentinel like HostedAgentStatus does) so deriveStatus() keeps the agent in
  // 'provisioning' until a recognized progress value is stored, rather than
  // optimistically treating an unknown/new/typo'd value as fully set up.
  return (KNOWN_SETUP_PROGRESS as readonly string[]).includes(value)
    ? (value as SetupProgress)
    : 'agent_created';
}

// =============================================================================
// READ HOOKS
// =============================================================================

/**
 * Get all hosted agent connections from connections.json
 */
export function useHostedAgents() {
  const appInfo = useAppInfoForConnections();
  const connectionsQuery = useConnectionsJson(appInfo);
  const queryClient = useQueryClient();
  const completedDeletions = useHostedAgentDeletionStore((state) => state.completedDeletions);
  const pendingRecreations = useHostedAgentDeletionStore((state) => state.pendingRecreations);
  const cleanupFailures = useHostedAgentDeletionStore((state) => state.cleanupFailures);
  const appId = appInfo?.appId;
  const appName = appInfo?.appName;
  const projectName = appInfo?.projectName;
  const refetchConnections = connectionsQuery.refetch;
  const completedForApp = useMemo(() => {
    if (!appId) return [];
    const prefix = `${appId}::`;
    return Object.entries(completedDeletions)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, at]) => ({
        key,
        at,
        connectionKey: key.slice(prefix.length),
      }));
  }, [appId, completedDeletions]);
  const recreationsForApp = useMemo(() => {
    if (!appId) return [];
    const prefix = `${appId}::`;
    return Object.entries(pendingRecreations)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, at]) => ({
        key,
        at,
        connectionKey: key.slice(prefix.length),
      }));
  }, [appId, pendingRecreations]);
  const failuresForApp = useMemo(() => {
    if (!appId) return [];
    const prefix = `${appId}::`;
    return Object.entries(cleanupFailures)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, failure]) => ({
        key,
        at: failure.at,
        connectionKey: key.slice(prefix.length),
      }));
  }, [appId, cleanupFailures]);

  useEffect(() => {
    if (!appName || !projectName || completedForApp.length === 0) return;

    let cancelled = false;
    void queryClient.invalidateQueries({
      queryKey: ['connections-draft', projectName.toLowerCase(), appName.toLowerCase()],
    });
    void refetchConnections()
      .then(({ data, isSuccess }) => {
        // A failed refetch resolves with the previous (stale) `data`, so only
        // acknowledge a deletion tombstone off a fresh, SUCCESSFUL read. Absence
        // from a stale cache is not proof the deletion has propagated.
        if (cancelled || !isSuccess || data === undefined) return;
        for (const completion of completedForApp) {
          const record = data.hostedAgentConnections?.[completion.connectionKey];
          const recreatedAt = record?.configuration.createdAt
            ? Date.parse(record.configuration.createdAt)
            : Number.NaN;
          if (!record || (!Number.isNaN(recreatedAt) && recreatedAt > completion.at)) {
            useHostedAgentDeletionStore
              .getState()
              .acknowledgeCompletedDeletion(completion.key, completion.at);
          }
        }
      })
      .catch((error: unknown) => {
        telemetryService.trackException(
          error instanceof Error ? error : new Error(String(error)),
          { phase: 'cleanup-reconcile' },
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    appName,
    completedForApp,
    projectName,
    queryClient,
    refetchConnections,
  ]);

  useEffect(() => {
    const data = connectionsQuery.data;
    if (data === undefined || completedForApp.length === 0) return;

    // The cached snapshot may pre-date the deletion, so absence here is NOT
    // reliable proof the tombstone can be cleared — only the successful refetch
    // effect above acknowledges on absence. Here we acknowledge solely on the
    // positive signal of a genuine recreation (a record newer than the deletion).
    for (const completion of completedForApp) {
      const record = data.hostedAgentConnections?.[completion.connectionKey];
      const recreatedAt = record?.configuration.createdAt
        ? Date.parse(record.configuration.createdAt)
        : Number.NaN;
      if (record && !Number.isNaN(recreatedAt) && recreatedAt > completion.at) {
        useHostedAgentDeletionStore
          .getState()
          .acknowledgeCompletedDeletion(completion.key, completion.at);
      }
    }
  }, [completedForApp, connectionsQuery.data]);

  useEffect(() => {
    const data = connectionsQuery.data;
    if (data === undefined || failuresForApp.length === 0) return;

    // A persisted cleanup-failure marker is tab-scoped (sessionStorage) and, unlike
    // the completion/recreation guards, is otherwise never reconciled against fresh
    // production data. If this tab missed the ephemeral recreation broadcast (e.g. it
    // was in bfcache while the recreating tab closed), a newer same-key agent loads
    // here while the stale marker remains — mislabeling the new agent as deleted and
    // letting "Retry cleanup" delete the freshly-recreated Foundry agent. A record
    // whose createdAt is strictly after the failure is proof the agent was recreated,
    // so treat it as a recreation (which clears the failure AND any deletion tombstone
    // and broadcasts the recreation to peer tabs) and acknowledge it locally, since
    // this tab already holds the canonical record and needs no confirming refetch. A
    // missing or unparseable createdAt is treated as "not newer", so a legitimately
    // current failure is never hidden.
    for (const failure of failuresForApp) {
      const record = data.hostedAgentConnections?.[failure.connectionKey];
      const createdAt = record?.configuration.createdAt
        ? Date.parse(record.configuration.createdAt)
        : Number.NaN;
      if (record && !Number.isNaN(createdAt) && createdAt > failure.at) {
        const store = useHostedAgentDeletionStore.getState();
        store.markAgentRecreated(failure.key);
        store.acknowledgeAgentRecreated(failure.key);
      }
    }
  }, [connectionsQuery.data, failuresForApp]);

  useEffect(() => {
    if (!appName || !projectName || recreationsForApp.length === 0) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    // A recreated agent stays hidden until a FRESH successful read confirms the
    // record (presence in the ambient cache is not enough — it may still be the
    // stale pre-deletion record with the same key). A single one-shot refetch
    // that fails, or that wins the race before the record is visible, would
    // otherwise wedge the guard for the whole tab session. Bounded retry lets a
    // transient first read recover without ever revealing a stale record.
    const MAX_ATTEMPTS = 5;
    const RETRY_DELAY_MS = 1500;

    const attempt = (n: number): void => {
      void queryClient.invalidateQueries({
        queryKey: ['connections-draft', projectName.toLowerCase(), appName.toLowerCase()],
      });
      void refetchConnections()
        .then(({ data, isSuccess }) => {
          if (cancelled) return;
          let allResolved = isSuccess && data !== undefined;
          if (isSuccess && data !== undefined) {
            for (const recreation of recreationsForApp) {
              if (data.hostedAgentConnections?.[recreation.connectionKey]) {
                useHostedAgentDeletionStore
                  .getState()
                  .acknowledgeAgentRecreated(recreation.key, recreation.at);
              } else {
                allResolved = false;
              }
            }
          }
          if (!allResolved && n < MAX_ATTEMPTS) {
            retryTimer = setTimeout(() => attempt(n + 1), RETRY_DELAY_MS);
          }
        })
        .catch((error: unknown) => {
          telemetryService.trackException(
            error instanceof Error ? error : new Error(String(error)),
            { phase: 'recreation-reconcile' },
          );
          if (!cancelled && n < MAX_ATTEMPTS) {
            retryTimer = setTimeout(() => attempt(n + 1), RETRY_DELAY_MS);
          }
        });
    };
    attempt(1);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    appName,
    projectName,
    queryClient,
    recreationsForApp,
    refetchConnections,
  ]);

  const agents = useMemo(() => {
    const config = connectionsQuery.data;
    if (!config?.hostedAgentConnections) return [];
    return Object.entries(config.hostedAgentConnections)
      .filter(([key]) => {
        const deletionKey = hostedAgentDeletionKey(appId, key);
        return completedDeletions[deletionKey] === undefined
          && pendingRecreations[deletionKey] === undefined;
      })
      .map(([key, conn]) => ({
        key,
        ...conn,
        // connections.json is externally authored — normalize casing so status/label
        // mappings and health-poll gating are resilient to values like "Connected".
        status: normalizeStatus(conn.status),
        setupProgress: normalizeSetupProgress(conn.setupProgress),
      }));
  }, [appId, completedDeletions, connectionsQuery.data, pendingRecreations]);

  // The RAW hosted-agent bucket exactly as stored in connections.json (unnormalized, no
  // synthetic `key`). Exposed so the workflow tab can keep connectionStore's serialization
  // baseline in sync after a cross-tab refetch. `undefined` until the first successful fetch:
  // fetchConnectionsJson now lets non-404 failures reject (it no longer swallows them to {}), so a
  // transient error leaves `data` undefined (or last-good on a background refetch) instead of an
  // empty {} — which keeps callers from clobbering the saved baseline with an empty set on failure.
  const hostedAgentRecords = useMemo(() => {
    if (!connectionsQuery.data) return undefined;
    // Do not replace connectionStore's serialization baseline with a filtered,
    // incomplete bucket while another tab's recreation is being reconciled.
    if (recreationsForApp.length > 0) return undefined;
    return Object.fromEntries(
      Object.entries(connectionsQuery.data.hostedAgentConnections ?? {})
        .filter(([key]) => {
          const deletionKey = hostedAgentDeletionKey(appId, key);
          return completedDeletions[deletionKey] === undefined
            && pendingRecreations[deletionKey] === undefined;
        }),
    );
  }, [appId, completedDeletions, connectionsQuery.data, pendingRecreations, recreationsForApp.length]);

  return {
    agents,
    hostedAgentRecords,
    isLoading: connectionsQuery.isLoading,
    // isFetching is true for BOTH the initial load and any background refetch (e.g. the
    // connection tab's focus/visibility refresh). Callers that gate a create affordance must use
    // this — not isLoading (which is false during a background refetch) — so the affordance stays
    // disabled while a stale list is being refreshed, preserving the duplicate-creation safeguard.
    isFetching: connectionsQuery.isFetching,
    error: connectionsQuery.error,
    refetch: connectionsQuery.refetch,
  };
}

/**
 * Get a single hosted agent by name from connections.json
 */
export function useHostedAgent(agentName: string | undefined) {
  const { agents, isLoading, error } = useHostedAgents();

  const agent = useMemo(
    () => agents.find((a) => a.configuration.agentName === agentName),
    [agents, agentName],
  );

  return { agent, isLoading, error };
}

/** Result of resolving the image to provision a hosted agent with. */
export interface ResolvedHostedAgentImage {
  /** Image reference to send to Foundry. Always usable. */
  image: string;
  /** True when `image` came from the app's override setting. */
  isOverride: boolean;
  /** A rejected override value, so the UI can say why it was ignored. */
  invalidOverride?: string;
  /**
   * True while the app's settings are in flight, when an override may exist but
   * isn't visible yet. Callers that provision must wait, or they would resolve
   * to the default and silently ignore a configured override.
   */
  isLoading: boolean;
}

/**
 * Resolves the app's `Otto.HostedAgent.ImageUri` setting, else the default.
 * Never throws or blocks: no app context or a failed fetch yields the default.
 */
export function useHostedAgentImage(): ResolvedHostedAgentImage {
  const appInfo = useAppInfoForConnections();
  const settingsQuery = useEnvironmentSettings(appInfo?.projectName ?? '', appInfo?.appName ?? '', /* isDraftMode */ false);

  // React Query's isLoading is `isPending && isFetching`, so a disabled query
  // (no app selected) reports false rather than pending forever. isPending or
  // !isSuccess would strand callers that gate on this.
  const isLoading = settingsQuery.isLoading;

  return useMemo(() => {
    // Matched case-insensitively, like app settings elsewhere in the platform.
    const raw = settingsQuery.data
      ?.find((setting) => setting.name?.toLowerCase() === HOSTED_AGENT_IMAGE_SETTING.toLowerCase())
      ?.value
      ?.trim();

    if (!raw) {
      return { image: DEFAULT_HOSTED_AGENT_IMAGE, isOverride: false, isLoading };
    }

    if (!raw.startsWith(HOSTED_AGENT_IMAGE_PREFIX)) {
      return { image: DEFAULT_HOSTED_AGENT_IMAGE, isOverride: false, invalidOverride: raw, isLoading };
    }

    return { image: raw, isOverride: true, isLoading };
  }, [settingsQuery.data, isLoading]);
}

// =============================================================================
// HEALTH CHECK HOOK
// =============================================================================

/**
 * Poll health status for a hosted agent.
 * Only polls when agent is in 'connected' or 'provisioning' status.
 */
export function useHostedAgentHealth(connection: HostedAgentConnection | undefined, active = true) {
  const { getFoundryToken } = useAuth();

  return useQuery({
    queryKey: hostedAgentKeys.health(connection?.configuration.agentName ?? ''),
    queryFn: async () => {
      if (!connection) throw new Error('No connection');
      const token = await getFoundryToken();
      if (!token) throw new Error('No Foundry token');
      return runHealthCheck(
        token,
        connection.endpoints.invocationsUrl,
        connection.configuration.imageTag,
      );
    },
    enabled: active && !!connection && (connection.status === 'connected' || connection.status === 'provisioning'),
    refetchInterval: connection?.status === 'provisioning' ? 15_000 : 60_000,
    staleTime: 10_000,
  });
}

// =============================================================================
// DIAGNOSTICS HOOK
// =============================================================================

/**
 * Run diagnostics for a hosted agent. Not auto-fetched — triggered on demand.
 */
export function useHostedAgentDiagnostics(connection: HostedAgentConnection | undefined) {
  const { getArmToken, getFoundryToken, getOttoToken } = useAuth();
  const { selectedProject, selectedApp } = useAppStore();

  return useQuery({
    queryKey: hostedAgentKeys.diagnostics(connection?.configuration.agentName ?? ''),
    queryFn: async (): Promise<DiagnosticResults> => {
      if (!connection) throw new Error('No connection');
      const [armToken, foundryToken, ottoToken] = await Promise.all([getArmToken(), getFoundryToken(), getOttoToken()]);
      if (!armToken || !foundryToken) throw new Error('Missing tokens');

      const appContext = ottoToken && selectedProject && selectedApp
        ? { ottoToken, projectName: selectedProject, appName: selectedApp.name }
        : undefined;

      return runDiagnostics(armToken, foundryToken, connection, appContext);
    },
    enabled: false, // Only runs when manually refetched
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Save a hosted agent connection to connections.json AND connections-draft.json.
 * Both must be updated so the designer (which reads draft) sees the new connection
 * immediately, while production connections.json is also kept in sync.
 */
export function useSaveHostedAgent() {
  const appInfo = useAppInfoForConnections();
  const connectionsQuery = useConnectionsJson(appInfo);
  const saveConnections = useSaveConnectionsJson(appInfo);
  const saveDraftConnections = useSaveDraftConnectionsJson(appInfo);
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { key: string; connection: HostedAgentConnection }) => {
      // Seed prod from connections.json and draft from a FRESH connections-draft.json
      // read so a draft-only connection (e.g. an OpenAI agentConnection created in
      // draft mode) is preserved instead of being clobbered by the production baseline.
      const prodConfig = await resolveConnectionsConfig(connectionsQuery);
      const draftConfig = await readDraftConnectionsBaseline(appInfo, getOttoToken);
      const { prod, draft } = buildHostedAgentPayloads(prodConfig, draftConfig, (hosted) => {
        hosted[params.key] = params.connection;
        return hosted;
      });
      await Promise.all([
        saveConnections.mutateAsync(prod),
        saveDraftConnections.mutateAsync(draft),
      ]);
      const deletionKey = hostedAgentDeletionKey(appInfo?.appId, params.key);
      const deletionState = useHostedAgentDeletionStore.getState();
      // A successful intentional save supersedes deletion state for this key.
      // Broadcast a distinct recreation record rather than a deletion-complete
      // tombstone, which would hide the newly saved agent in other tabs.
      deletionState.markAgentRecreated(deletionKey);
      useHostedAgentDeletionStore.getState().acknowledgeAgentRecreated(deletionKey);
      // Keep the connection store baseline in sync so a later workflow save
      // (exportToConnectionsConfig seeds hostedAgentConnections from
      // originalConnectionsConfig) doesn't drop this out-of-band change.
      useConnectionStore.getState().setHostedAgentConnectionRecords(prod.hostedAgentConnections ?? {});
      // NOTE: We deliberately do NOT register a connection reference here. This hook runs only in
      // the app-scoped Agents tab (HostedAgentWizard), which has no canvas — and
      // addConnectionReference calls markDirty(), which syncFromConnectionsConfig then preserves
      // into the next workflow load, giving that workflow a false unsaved state and a spurious
      // draft autosave. The originating designer registers the reference when the author selects
      // the agent from the dropdown (HostedAgentConnectionTab.handleSelectAgent).
      telemetryService.trackEvent('hostedAgent.saved', {
        agentName: params.connection.configuration.agentName,
        status: params.connection.status,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostedAgentKeys.all });
    },
  });
}

/**
 * Update a hosted agent connection's status or progress
 */
export function useUpdateHostedAgent() {
  const appInfo = useAppInfoForConnections();
  const saveConnections = useSaveConnectionsJson(appInfo);
  const saveDraftConnections = useSaveDraftConnectionsJson(appInfo);
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { key: string; updates: Partial<HostedAgentConnection> }) => {
      if (!appInfo?.projectName || !appInfo?.appName) {
        throw new Error('Project and app context are required to update a hosted agent.');
      }
      const deletionKey = hostedAgentDeletionKey(appInfo.appId, params.key);
      if (useHostedAgentDeletionStore.getState().completedDeletions[deletionKey] !== undefined) {
        throw new Error(`Hosted agent ${params.key} was deleted and cannot be updated.`);
      }
      const draftConfig = await readDraftConnectionsBaseline(appInfo, getOttoToken);
      // Read production last so the authoritative existence check is as close
      // as possible to the whole-file writes.
      const prodConfig = (await fetchConnectionsJsonStrictWithETag({
        projectName: appInfo.projectName,
        appName: appInfo.appName,
        getToken: getOttoToken,
      })).config;
      // A cross-tab delete can complete while either baseline read is in flight.
      // Recheck immediately before starting writes so an arriving completion
      // tombstone cannot be bypassed by a mutation that began moments earlier.
      if (useHostedAgentDeletionStore.getState().completedDeletions[deletionKey] !== undefined) {
        throw new Error(`Hosted agent ${params.key} was deleted and cannot be updated.`);
      }
      // Production is the canonical source for the existence check + merge base
      // (the Agents list/picker read production).
      const existing = prodConfig.hostedAgentConnections?.[params.key];
      if (!existing) throw new Error(`Hosted agent ${params.key} not found`);



      // Per-file modify/write with transient-5xx retries. We RE-ISSUE the same
      // write on each retry and deliberately do NOT re-read between attempts: a
      // transient 5xx often means the deploy landed in storage but the container
      // failed to restart, so a re-read would see the update already applied and
      // skip the retry — reporting success while the service is still unhealthy.
      // Re-writing drives the restart to completion, or exhausts the retries and
      // throws so the caller (e.g. the provisioning pipeline) fails the step.
      // prod and draft retry INDEPENDENTLY, so a failure on one never re-posts the
      // other's snapshot.
      const applyToFile = async (
        write: (config: ConnectionsConfiguration) => Promise<unknown>,
        config: ConnectionsConfiguration,
        throwIfAbsent: boolean,
      ): Promise<ConnectionsConfiguration | null> => {
        const current = config.hostedAgentConnections?.[params.key];
        if (!current) {
          // Agent removed concurrently (e.g. deleted in another tab): never
          // resurrect it. Production absence is a hard error for the caller.
          if (throwIfAbsent) throw new Error(`Hosted agent ${params.key} not found`);
          return null;
        }
        if (hostedAgentUpdatesApplied(current, params.updates)) return config;
        const nextConfig: ConnectionsConfiguration = {
          ...config,
          hostedAgentConnections: {
            ...(config.hostedAgentConnections ?? {}),
            [params.key]: { ...current, ...params.updates },
          },
        };
        for (let attempt = 0; ; attempt++) {
          // A cross-tab delete can complete during a long backoff; re-check right
          // before every write so a retry cannot re-add a tombstoned agent.
          if (useHostedAgentDeletionStore.getState().completedDeletions[deletionKey] !== undefined) {
            throw new Error(`Hosted agent ${params.key} was deleted and cannot be updated.`);
          }
          try {
            await write(nextConfig);
            return nextConfig;
          } catch (error) {
            if (!isTransientServerError(error) || attempt >= UPDATE_RETRY_DELAYS_MS.length) {
              throw error;
            }
            await delay(UPDATE_RETRY_DELAYS_MS[attempt]!);
          }
        }
      };

      // prod and draft retry INDEPENDENTLY; a transient failure on one never
      // re-posts the other's snapshot. A non-transient failure (or exhausted
      // retries) rejects so the caller — e.g. the provisioning pipeline — can
      // treat the step as failed rather than silently advance.
      const [prodResult] = await Promise.all([
        applyToFile(
          (config) => saveConnections.mutateAsync(config),
          prodConfig,
          true,
        ),
        applyToFile(
          (config) => saveDraftConnections.mutateAsync(config),
          draftConfig,
          false,
        ),
      ]);

      if (prodResult) {
        useConnectionStore.getState().setHostedAgentConnectionRecords(prodResult.hostedAgentConnections ?? {});
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostedAgentKeys.all });
    },
  });
}

/**
 * Delete the complete Foundry agent, then remove its local connection records.
 */
export function useDeleteHostedAgent() {
  const appInfo = useAppInfoForConnections();
  const { getOttoToken, getArmToken, getFoundryToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      key,
      connection,
      onProgress,
    }: DeleteHostedAgentParams): Promise<HostedAgentDeleteResult> => {
      const agentName = connection.configuration.agentName;
      const foundryToken = await getFoundryToken();
      if (!foundryToken) {
        const error = new Error('Could not acquire a Foundry token. No resources were deleted.');
        telemetryService.trackException(error, { phase: 'token', agentName });
        throw error;
      }

      const warnings: HostedAgentDeleteWarning[] = [];

      // Delete the authoritative (billable) Foundry agent FIRST. This is the
      // idempotent, blocking step: if it fails, we abort before touching RBAC or
      // local state so a surviving agent keeps its identity role assignments.
      onProgress?.({ phase: 'foundry', status: 'started' });
      try {
        await deleteHostedAgent(
          foundryToken,
          connection.endpoints.projectEndpoint,
          agentName,
        );
        onProgress?.({ phase: 'foundry', status: 'completed' });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        onProgress?.({ phase: 'foundry', status: 'failed', error: message });
        telemetryService.trackException(
          error instanceof Error ? error : new Error(message),
          { phase: 'foundry', agentName },
        );
        throw error;
      }

      // Foundry agent is gone. Best-effort RBAC cleanup runs next as warn-only:
      // an RBAC failure here must not resurrect a stale local record for an agent
      // that no longer exists in Foundry.
      // The blueprint principal is no longer granted a role during provisioning
      // (ARM rejects it outside a brief post-creation window), but agents created
      // before that change may still hold one — keep it in the cleanup set.
      const requestedIdentities = [
        { identity: 'blueprint' as const, principalId: connection.identity.blueprintPrincipalId },
        { identity: 'instance' as const, principalId: connection.identity.instancePrincipalId },
      ].filter((item): item is { identity: 'blueprint' | 'instance'; principalId: string } => !!item.principalId);

      // principalIds are read from connections.json (externally authored) and get
      // interpolated into an ARM `$filter=principalId eq '<id>'` query inside
      // removeRbacAssignments. Only a well-formed GUID is a legitimate principal ID,
      // so reject anything else up front: a crafted value such as `x' or principalId ne 'x`
      // would otherwise broaden the filter and delete unrelated role assignments with the
      // user's ARM token. An invalid value falls through to the same warn-only RBAC result
      // a failed removal produces.
      const validIdentities = requestedIdentities.filter(({ principalId }) => GUID_PATTERN.test(principalId));
      const invalidIdentities = requestedIdentities.filter(({ principalId }) => !GUID_PATTERN.test(principalId));

      if (requestedIdentities.length > 0) {
        onProgress?.({ phase: 'rbac', status: 'started' });

        for (const { identity } of invalidIdentities) {
          const message = `Skipped role-assignment cleanup for the ${identity} identity because its principal ID is not a valid GUID.`;
          warnings.push({ phase: 'rbac', identity, message });
          // Do not echo the raw (untrusted) principalId value into telemetry.
          telemetryService.trackException(new Error(message), { phase: 'rbac', identity, agentName });
        }

        let accountScope: string | null = null;
        if (validIdentities.length > 0) {
          try {
            accountScope = buildAccountScope(
              connection.configuration.subscriptionId,
              connection.configuration.resourceGroup,
              connection.configuration.aiAccountName,
            );
          } catch {
            const message = 'Skipped role-assignment cleanup because the configured Azure resource scope is invalid.';
            warnings.push({ phase: 'rbac', identity: 'all', message });
            telemetryService.trackException(new Error(message), {
              phase: 'rbac',
              identity: 'all',
              agentName,
            });
          }
        }

        let armToken: string | null = null;
        let armTokenError: unknown;
        if (validIdentities.length > 0 && accountScope) {
          try {
            armToken = await getArmToken();
          } catch (error) {
            armTokenError = error;
          }
        }

        if (validIdentities.length > 0 && accountScope && !armToken) {
          const detail = armTokenError instanceof Error ? ` ${armTokenError.message}` : '';
          const message = `Could not acquire an Azure Resource Manager token; role assignments may remain.${detail}`;
          warnings.push({ phase: 'rbac', identity: 'all', message });
          telemetryService.trackException(
            armTokenError instanceof Error ? armTokenError : new Error(message),
            { phase: 'rbac', identity: 'all', agentName },
          );
        } else if (armToken && accountScope) {
          // Scope removal to ONLY the role this feature grants agent identities
          // (Cognitive Services OpenAI User). Even if a crafted/stale
          // connections.json named a different principal, no other role assignment
          // it happens to hold at this scope would be touched.
          const results = await Promise.allSettled(
            validIdentities.map(({ principalId }) => removeRbacAssignments(
              armToken,
              accountScope,
              principalId,
              [ROLE_COGNITIVE_SERVICES_OPENAI_USER],
            )),
          );
          results.forEach((result, index) => {
            if (result.status === 'fulfilled') return;
            const identity = validIdentities[index]!.identity;
            const message = result.reason instanceof Error ? result.reason.message : String(result.reason);
            warnings.push({ phase: 'rbac', identity, message });
            telemetryService.trackException(
              result.reason instanceof Error ? result.reason : new Error(message),
              { phase: 'rbac', identity, agentName },
            );
          });
        }

        onProgress?.(
          warnings.length > 0
            ? { phase: 'rbac', status: 'warning', error: 'Some agent role assignments could not be removed.' }
            : { phase: 'rbac', status: 'completed' },
        );
      }

      onProgress?.({ phase: 'local', status: 'started' });
      try {
        // Deletion writes entire files. Strict fresh reads prevent both stale
        // cache overwrites and a failed read being mistaken for an empty file.
        if (!appInfo?.projectName || !appInfo?.appName) {
          throw new Error(
            'Project and app context are required to clean up hosted-agent connections.',
          );
        }
        const readOptions = {
          projectName: appInfo.projectName,
          appName: appInfo.appName,
          getToken: getOttoToken,
        };
        // Whole-file read/modify/write with a bounded retry loop.
        //
        // Each attempt reads a fresh baseline of each file, removes the agent, then
        // PUTs the whole file back through the app's DEFAULT VFS write path (no
        // caller `If-Match`). That default path performs its own HEAD →
        // `If-Match: <etag>`, falling back to `If-Match: *` ("must exist") when the
        // backend's HEAD yields no usable ETag, and retries once on a 412. This is
        // the exact write contract every other connection write in the app uses.
        //
        // We deliberately do NOT attempt client-side optimistic concurrency with a
        // caller-supplied concrete `If-Match` here: this Logic Apps VFS backend
        // makes true CAS impossible. Its HEAD returns 404 for files that GET returns
        // 200 for, so a runtime ETag cannot be captured before the write; and its
        // GET ETag lives in a different namespace than the PUT precondition, so
        // feeding it into `If-Match` is rejected unconditionally (issue #2603's
        // permanent-412 regression). The default `If-Match: *` write is therefore
        // the strongest precondition this backend actually honors.
        const MAX_LOCAL_ATTEMPTS = 5;
        const backoffMs = [0, 200, 500, 900];
        for (let attempt = 0; ; attempt++) {
          try {
            const prodRead = await fetchConnectionsJsonStrictWithETag(readOptions);
            const draftRead = await fetchConnectionsDraftJsonStrictWithETag(readOptions);
            // Remove the key from EACH baseline independently so deleting from prod
            // doesn't overwrite (and drop) draft-only connections in connections-draft.json.
            // Whether each freshly-read baseline still contains the agent. A file that
            // no longer has `key` needs no write: this happens when a prior attempt's
            // whole-file write committed on the server but its response was lost, or when
            // draft cleanup already succeeded before a later production write failed.
            // Re-PUTing an unchanged file is a pure no-op that can still fail (keeping
            // cleanup marked failed) and needlessly risks clobbering unrelated concurrent
            // edits — so skip a baseline that already lacks the agent.
            const prodHasKey = !!prodRead.config.hostedAgentConnections?.[key];
            const draftHasKey = !!draftRead.config.hostedAgentConnections?.[key];
            const { prod, draft } = buildHostedAgentPayloads(prodRead.config, draftRead.config, (hosted) => {
              delete hosted[key];
              return hosted;
            });
            // Persist draft first. If it fails, production still contains the agent
            // and both UI entry points remain available for retry. Once draft is
            // clean, removing production is the final local commit.
            //
            // When the draft file does NOT exist (`absent`), the strict reader
            // synthesized its config from production. Writing that back would create
            // a draft file the app never had, so skip the draft write entirely —
            // there is nothing to clean up in a missing draft.
            if (!draftRead.absent && draftHasKey) {
              await saveDraftConnectionsJson({
                ...readOptions,
                connectionsConfig: draft,
              });
            }
            // An absent (404) production file — or one that no longer contains the
            // agent — has nothing to delete.
            if (!prodRead.absent && prodHasKey) {
              await saveConnectionsJson({
                ...readOptions,
                connectionsConfig: prod,
              });
            }
            useConnectionStore.getState().setHostedAgentConnectionRecords(prod.hostedAgentConnections ?? {});
            break;
          } catch (error) {
            const retryable = isConcurrencyConflict(error) || isTransientServerError(error);
            if (retryable && attempt < MAX_LOCAL_ATTEMPTS - 1) {
              await delay(backoffMs[Math.min(attempt, backoffMs.length - 1)] ?? 0);
              continue;
            }
            throw error;
          }
        }
        // Refresh the connection caches the save hooks would normally invalidate,
        // since the deletion writes go through the service layer directly.
        const projectKey = appInfo.projectName.toLowerCase();
        const appKey = appInfo.appName.toLowerCase();
        void queryClient.invalidateQueries({ queryKey: ['connections', projectKey, appKey] });
        void queryClient.invalidateQueries({ queryKey: ['connections-draft', projectKey, appKey] });
        void queryClient.invalidateQueries({ queryKey: ['connections'] });
        // Local cleanup completed (possibly on retry) — clear any prior durable
        // "cleanup failed" marker so the Agent UI and designer stop showing an error.
        useHostedAgentDeletionStore
          .getState()
          .clearCleanupFailure(hostedAgentDeletionKey(appInfo.appId, key));
        onProgress?.({ phase: 'local', status: 'completed' });
      } catch (error) {
        // The raw error can originate from token acquisition or JSON parsing and
        // may embed provider URLs or reflected sensitive text. Sanitize before it
        // reaches any user-visible sink (progress dialog, thrown UI error) or the
        // durable failure marker, and send telemetry a stable, non-provider-authored
        // exception message (trackException does not sanitize the exception itself).
        const rawMessage = error instanceof Error ? error.message : String(error);
        const message = sanitizeErrorMessage(rawMessage);
        onProgress?.({ phase: 'local', status: 'failed', error: message });
        // Foundry deletion already succeeded, but local cleanup did not. Record a
        // durable, session-scoped marker so the Agents list, Agent detail page, and
        // the workflow designer surface a persistent error (and offer retry) instead
        // of showing the now-Foundry-gone agent as healthy.
        useHostedAgentDeletionStore
          .getState()
          .markCleanupFailed(hostedAgentDeletionKey(appInfo?.appId, key), {
            agentName,
            message,
            at: Date.now(),
          });
        telemetryService.trackException(
          new Error('Hosted agent local cleanup failed'),
          { phase: 'local', agentName, detail: message },
        );
        throw new Error(
          `The agent was deleted in AI Foundry, but local configuration cleanup failed: ${message}. Retry to finish local cleanup.`,
          { cause: error },
        );
      }

      telemetryService.trackEvent('hostedAgent.deleted', {
        agentName,
        rbacWarning: String(warnings.length > 0),
      });
      return { warnings };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hostedAgentKeys.all });
    },
  });
}

// =============================================================================
// STATUS HELPERS
// =============================================================================

/**
 * Map a health-check result to a HostedAgentStatus.
 *
 * This must be able to produce every post-provisioning status the UI actually
 * renders (see HostedAgentStatus). In particular `auth_failed` and `unreachable`
 * are first-class states with their own badges and card/panel affordances, so we
 * derive them here rather than leaving them as dead states only the backend could
 * ever set:
 *   - `auth_failed` — the container responded but the Logic App managed identity
 *                     could not authenticate to Foundry (HTTP 401/403, surfaced by
 *                     runHealthCheck as `model_auth: "auth_failed: HTTP ..."`).
 *   - `unreachable` — the health request itself failed (network error, timeout,
 *                     404/5xx), i.e. the container could not be reached at all.
 */
export function deriveStatus(
  health: HealthCheckResponse | undefined,
  setupProgress: string,
  healthErrored = false,
): HostedAgentStatus {
  // Agent is usable once RBAC is assigned or setup is fully complete
  if (setupProgress !== 'complete' && setupProgress !== 'rbac_assigned') return 'provisioning';
  // The health request threw (after react-query retries) → container unreachable.
  if (healthErrored) return 'unreachable';
  // Container answered but the MI → Foundry token was rejected.
  if (health?.model_auth?.startsWith('auth_failed')) return 'auth_failed';
  switch (health?.status) {
    case 'healthy': return 'connected';
    case 'version_mismatch': return 'version_mismatch';
    case 'unhealthy': return 'agent_error';
    default: return 'unknown';
  }
}
